import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db, firebaseEnabled } from '../config/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';

export interface Announcement {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'urgent';
  active: boolean;
  createdAt: string;
  expiresAt: string;
}

interface AnnouncementContextType {
  announcements: Announcement[];
  activeAnnouncements: Announcement[];
  addAnnouncement: (data: Omit<Announcement, 'id' | 'createdAt'>) => void;
  updateAnnouncement: (id: string, updates: Partial<Announcement>) => void;
  deleteAnnouncement: (id: string) => void;
  bannerHeight: number;
  setBannerHeight: (h: number) => void;
}

const STORAGE_KEY = 'cabinet_dentaire_announcements';
const COLLECTION = 'announcements';

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined);

export const AnnouncementProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [bannerHeight, setBannerHeight] = useState(0);

  // ─── Firestore real-time listener ───
  useEffect(() => {
    if (!firebaseEnabled || !db) {
      console.log('[Announcements] Running in localStorage mode');
      return;
    }

    console.log('[Announcements] Connecting to Firestore...');
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        })) as Announcement[];
        console.log(`[Announcements] ✓ Firestore sync — ${data.length} announcement(s) received`);
        setAnnouncements(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      },
      (error) => {
        console.error('[Announcements] ✗ Firestore listener error:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // ─── localStorage-only persistence ───
  useEffect(() => {
    if (!firebaseEnabled) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(announcements));
    }
  }, [announcements]);

  // ─── CSS variable for layout offset ───
  useEffect(() => {
    document.documentElement.style.setProperty('--announcement-offset', `${bannerHeight}px`);
  }, [bannerHeight]);

  const activeAnnouncements = announcements.filter(a => {
    if (!a.active) return false;
    if (a.expiresAt && new Date(a.expiresAt) < new Date()) return false;
    return true;
  });

  // ─── CRUD operations ───

  const addAnnouncement = useCallback((data: Omit<Announcement, 'id' | 'createdAt'>) => {
    if (firebaseEnabled && db) {
      console.log('[Announcements] Writing to Firestore...');
      addDoc(collection(db, COLLECTION), {
        message: data.message,
        type: data.type,
        active: data.active,
        expiresAt: data.expiresAt,
        createdAt: new Date().toISOString(),
      })
        .then(ref => console.log(`[Announcements] ✓ Saved to Firestore (id: ${ref.id})`))
        .catch(err => console.error('[Announcements] ✗ Add failed:', err));
    } else {
      setAnnouncements(prev => [{
        ...data,
        id: Date.now().toString(36) + Math.random().toString(36).substr(2, 9),
        createdAt: new Date().toISOString(),
      }, ...prev]);
    }
  }, []);

  const updateAnnouncement = useCallback((id: string, updates: Partial<Announcement>) => {
    if (firebaseEnabled && db) {
      const { id: _drop, ...clean } = updates as Record<string, any>;
      void _drop;
      updateDoc(doc(db, COLLECTION, id), clean)
        .catch(err => console.error('Update failed:', err));
    } else {
      setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
    }
  }, []);

  const deleteAnnouncement = useCallback((id: string) => {
    if (firebaseEnabled && db) {
      deleteDoc(doc(db, COLLECTION, id))
        .catch(err => console.error('Delete failed:', err));
    } else {
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    }
  }, []);

  return (
    <AnnouncementContext.Provider value={{
      announcements,
      activeAnnouncements,
      addAnnouncement,
      updateAnnouncement,
      deleteAnnouncement,
      bannerHeight,
      setBannerHeight,
    }}>
      {children}
    </AnnouncementContext.Provider>
  );
};

export const useAnnouncements = () => {
  const ctx = useContext(AnnouncementContext);
  if (!ctx) throw new Error('useAnnouncements must be used within AnnouncementProvider');
  return ctx;
};
